import type { FaturaPainel, Kpis } from './types.ts';
import { chaveCliente } from '../statusBoleto.ts';
import {
  DEFINICOES_KPI, faturasDoPainel, diasEmissaoAtePagamento, diasPagamentoAposVencimento, somaValorDevido, venceEmAte7Dias,
} from './definicoesKpi.ts';

function mediana(valores: number[]): number | null {
  if (valores.length === 0) return null;
  const ordenados = [...valores].sort((a, b) => a - b);
  const meio = Math.floor(ordenados.length / 2);
  return ordenados.length % 2 === 0 ? (ordenados[meio - 1] + ordenados[meio]) / 2 : ordenados[meio];
}

// Os 8 KPIs da spec (seção 6), calculados sobre o conjunto JÁ FILTRADO
// (chamar depois de filtrarFaturas). `hoje` é injetado para os testes serem
// determinísticos. Quais faturas entram em cada KPI vem de `definicoesKpi.ts`
// — a mesma definição que a lista de boletos e o Excel usam.
export function calcularKpis(entrada: FaturaPainel[], hoje: Date): Kpis {
  // Conta da própria Vigor sai de TODOS os indicadores — vencido, taxa, aging e
  // Top Devedores inclusive (ver ehContaInterna, solicitação 5955).
  const dados = faturasDoPainel(entrada);

  // Fatura sem boleto emitido (ainda no pipeline), de valor zero, ou sem
  // injeção no ciclo não foi faturada — fica fora de "emitido", do ticket
  // médio e do denominador da taxa de inadimplência.
  const cobrancas = DEFINICOES_KPI.emitido.filtrar(dados);
  const emitido = somaValorDevido(cobrancas);
  const qtdeBoletos = cobrancas.length;

  const recebido = somaValorDevido(DEFINICOES_KPI.recebido.filtrar(dados));

  const emAbertoLista = DEFINICOES_KPI.aVencer.filtrar(dados);
  const vencendo7dias = emAbertoLista.filter((f) => venceEmAte7Dias(f, hoje));

  const vencidoLista = DEFINICOES_KPI.vencido.filtrar(dados);
  const vencido = somaValorDevido(vencidoLista);
  const clientesVencidos = new Set(vencidoLista.map((f) => chaveCliente(f.cliente_nome)));

  // Duas leituras da mesma lista de boletos pagos, deliberadamente separadas:
  //   PRAZO   = emissão → pagamento (quanto o dinheiro demora a entrar)
  //   DESVIO  = pagamento − vencimento (pontualidade; negativo = paga adiantado)
  // Misturar as duas era o que fazia o card "Prazo médio de recebimento"
  // aparecer como -3,1 dias, que não é prazo nenhum.
  const pagos = DEFINICOES_KPI.prazoMedio.filtrar(dados);
  const prazos = pagos.map((f) => diasEmissaoAtePagamento(f)).filter((v): v is number => v !== null);
  const desvios = pagos.map((f) => diasPagamentoAposVencimento(f)!);
  // Boleto ainda por vencer no recorte: o período não fechou, e quem vai atrasar
  // ainda nem teve chance. Sem isto o mês em curso parece pontualidade recorde.
  const prazoAindaMaturando = emAbertoLista.length > 0;
  const pagosComAtraso = DEFINICOES_KPI.recuperado.filtrar(dados);

  return {
    emitido,
    qtdeBoletos,
    recebido,
    recebidoPctEmitido: emitido > 0 ? recebido / emitido : 0,
    emAberto: somaValorDevido(emAbertoLista),
    emAbertoValorVencendo7dias: somaValorDevido(vencendo7dias),
    emAbertoQtdeVencendo7dias: vencendo7dias.length,
    vencido,
    vencidoQtdeBoletos: vencidoLista.length,
    vencidoClientesDistintos: clientesVencidos.size,
    taxaInadimplencia: emitido > 0 ? vencido / emitido : 0,
    ticketMedio: qtdeBoletos > 0 ? emitido / qtdeBoletos : 0,
    prazoMedioRecebimentoDias: prazos.length > 0 ? prazos.reduce((s, v) => s + v, 0) / prazos.length : null,
    prazoMedianoRecebimentoDias: mediana(prazos),
    desvioMedioVencimentoDias: desvios.length > 0 ? desvios.reduce((s, v) => s + v, 0) / desvios.length : null,
    desvioMedianoVencimentoDias: mediana(desvios),
    prazoAindaMaturando,
    recuperado: somaValorDevido(pagosComAtraso),
    recuperadoQtdePagosComAtraso: pagosComAtraso.length,
  };
}
